import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";

function relTime(iso, t) {
  if (!iso) return t ? t("stock_feed.never") : "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return t ? t("stock_feed.rel_seconds", { count: Math.max(1, Math.round(diff)) }) : `${Math.round(diff)}s`;
  if (diff < 3600) return t ? t("stock_feed.rel_minutes", { count: Math.max(1, Math.round(diff / 60)) }) : `${Math.round(diff / 60)}m`;
  if (diff < 86400) return t ? t("stock_feed.rel_hours", { count: Math.max(1, Math.round(diff / 3600)) }) : `${Math.round(diff / 3600)}h`;
  return d.toLocaleString();
}
function nextTime(iso, t) {
  if (!iso) return t ? t("stock_feed.never") : "—";
  const d = new Date(iso);
  const diff = (d.getTime() - Date.now()) / 1000;
  if (diff <= 0) return t ? t("stock_feed.due") : "due";
  if (diff < 3600) return t ? t("stock_feed.in_minutes", { count: Math.max(1, Math.round(diff / 60)) }) : `in ${Math.round(diff / 60)}m`;
  if (diff < 86400) return t ? t("stock_feed.in_hours", { count: Math.max(1, Math.round(diff / 3600)) }) : `in ${Math.round(diff / 3600)}h`;
  return d.toLocaleString();
}

/**
 * Feed status card. Shown when a feed exists. Displays the last sync summary
 * (added / updated / marked_unavailable / errors), "next sync" ETA, a
 * "Sincronizza ora" trigger, and a clear "Sincronizzazione simulata" badge
 * when the feed is pointed at the built-in demo endpoints.
 */
export default function StockFeedStatusCard({
  scope = "dealer",
  dealerId,
  feed,
  onSynced,
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(feed || null);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => setCurrent(feed || null), [feed]);

  const syncNow = async () => {
    setSyncing(true);
    setErr(null);
    try {
      const url =
        scope === "admin"
          ? `/admin/dealers/${dealerId}/stock-feed/sync-now`
          : `/dealers/me/stock-feed/sync-now`;
      const r = await api.post(url);
      setCurrent((c) => ({ ...(c || {}), last_result: r.data.result, last_sync_at: new Date().toISOString() }));
      if (onSynced) onSynced(r.data.result);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  if (!current) return null;
  const r = current.last_result;

  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-5 md:p-6"
      data-testid={`stock-feed-status-${scope}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <Activity className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
              {t("stock_feed.status_overline")}
            </p>
            {current.simulated && (
              <span
                data-testid="stock-feed-simulated-badge"
                className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-semibold"
              >
                {t("stock_feed.simulated_badge")}
              </span>
            )}
            <span
              className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold border ${
                current.enabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-500 border-slate-200"
              }`}
            >
              {current.enabled ? t("stock_feed.enabled") : t("stock_feed.disabled")}
            </span>
          </div>
          <h3 className="mt-0.5 font-heading font-semibold text-base tracking-tight text-slate-950 truncate">
            {current.url}
          </h3>
          <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t("stock_feed.last_sync")}:{" "}
              <span
                className="font-semibold text-slate-800"
                data-testid="stock-feed-last-sync"
              >
                {relTime(current.last_sync_at, t)}
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              {t("stock_feed.next_sync")}:{" "}
              <span
                className="font-semibold text-slate-800"
                data-testid="stock-feed-next-sync"
              >
                {nextTime(current.next_sync_at, t)}
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              {t("stock_feed.every")}: <span className="font-semibold text-slate-800">{current.interval_hours}h</span>
            </span>
          </div>
        </div>
        <Button
          type="button"
          onClick={syncNow}
          disabled={syncing}
          data-testid="stock-feed-sync-now"
          className="rounded-full bg-slate-950 hover:bg-slate-800 text-white text-xs font-semibold h-9 px-4"
        >
          {syncing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
          ) : (
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
          )}
          {t("stock_feed.sync_now")}
        </Button>
      </div>

      {err && (
        <div
          data-testid="stock-feed-sync-error"
          className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {err}
        </div>
      )}

      {r && (
        <>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="stock-feed-result-tiles">
            <ResultTile label={t("stock_feed.added")} value={r.added} accent="text-emerald-600" testid="stock-feed-added" />
            <ResultTile label={t("stock_feed.updated")} value={r.updated} accent="text-blue-600" testid="stock-feed-updated" />
            <ResultTile label={t("stock_feed.unavailable")} value={r.marked_unavailable} testid="stock-feed-unavailable" />
            <ResultTile
              label={t("stock_feed.skipped_by_filter")}
              value={r.skipped_by_filter || 0}
              accent={(r.skipped_by_filter || 0) > 0 ? "text-amber-600" : ""}
              testid="stock-feed-skipped-by-filter"
            />
            <ResultTile label={t("stock_feed.errors")} value={r.errors?.length || 0} accent={(r.errors?.length || 0) > 0 ? "text-red-600" : ""} testid="stock-feed-errors-count" />
          </div>

          {r.errors && r.errors.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setShowErrors((s) => !s)}
                data-testid="stock-feed-errors-toggle"
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {showErrors ? t("stock_feed.hide_errors") : t("stock_feed.show_errors", { count: r.errors.length })}
                {showErrors ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showErrors && (
                <ul
                  data-testid="stock-feed-errors-list"
                  className="mt-3 space-y-1.5 max-h-56 overflow-auto"
                >
                  {r.errors.slice(0, 50).map((e, i) => (
                    <li
                      key={`${e.row}-${e.field}-${i}`}
                      className="text-[12px] rounded-lg bg-red-50 border border-red-100 px-3 py-1.5 text-red-800"
                    >
                      <span className="font-mono text-[10px] mr-2 text-red-700">
                        row {e.row} · {e.field}
                      </span>
                      {e.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ResultTile({ label, value, accent = "", testid }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`mt-0.5 font-heading font-bold text-xl tabular-nums ${accent || "text-slate-950"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}
